import type { MenuItem } from "@/VisualBuilder/components/LeftHandMenu/types.js";
import {
  ELEMENT_POSITIONING,
  type EXPERIENCE_PRIMARY_FILTER,
  SHARE_SCOPE,
  TAB_STATUSES,
  VB_MODE,
} from "@/constants/index.js";
import { ExperienceService, PublicVisualizationService } from "@/services/index.js";
import type { ThunkDispatch } from "@/store/hooks.js";
import type { State, Tag, VisualBuilderDataState } from "@/store/types.js";
import { fetchVisualBuilderData } from "@/utils/visualBuilder/fetchUtils.js";
import {
  addPresentationInitialValues,
  addPublicVisualizationInitialValues,
  addVisualizationInitialValues,
  buildFilteredPayload,
  buildSortedPayload,
  buildTransformedPayload,
  visualBuilderDataInitialState,
} from "@/utils/visualBuilder/payloadUtils.js";
import { getVisualBuilderMode, isPublicPage } from "@/utils/visualBuilder.js";

import { type VisualBuilderDataSelectorParams, visualBuilderDataSelector } from "./selectors.js";
import {
  GET_VISUAL_BUILDER_DATA,
  RESET_VISUAL_BUILDER_DATA,
  UPDATE_VISUAL_BUILDER_DATA,
  type VisualBuilderModeResult,
} from "./types.js";
import { setSimpleValue } from "../../simpleValues/actions.js";
import type { User } from "../currentUser/selectors.js";

function extractTabDependentPayload(payload: VisualBuilderDataState) {
  return {
    mediaItems: payload.mediaItems,
    assetCategoryHiddenMediaItems: payload.assetCategoryHiddenMediaItems,
    contentTypeHiddenMediaItems: payload.contentTypeHiddenMediaItems,
    filteredMediaItems: payload.filteredMediaItems,
    filteredTagMediaItems: payload.filteredTagMediaItems,
    filteredAssetCategoryMediaItems: payload.filteredAssetCategoryMediaItems,
    filteredContentTypeMediaItems: payload.filteredContentTypeMediaItems,
  };
}

/**
 * Primary action creator to get and initialize visual builder data
 * @param {Object} params - Configuration parameters
 * @param {Object} values - Initial values to set
 * @returns {Function} Thunk action function
 */
export const getVisualBuilderData = (
  params: VisualBuilderDataSelectorParams & {
    propertyMenuItems?: MenuItem[];
    prevTabStatus?: string;
  },
  values: {
    otherTag?: Tag;
    tabChanged?: boolean;
  } = {},
): ((dispatch: ThunkDispatch, getState: () => State) => Promise<void>) => {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { propertyId, experienceId, tabStatus, prevTabStatus, shareId, mediaItemId, propertyMenuItems } = params;
    const { key, mode } = getVisualBuilderMode(params) as VisualBuilderModeResult;

    const query = {};
    let initial = false;

    const fetchResult = await fetchVisualBuilderData({
      propertyId: Number(propertyId),
      experienceId: Number(experienceId),
      mediaItemId: Number(mediaItemId),
      mode,
      query,
    });

    const { contentTypes, tags, mediaItems, assetCategories, flags } = fetchResult;

    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    // Build initial payload
    const initialPayload = {
      ...visualBuilderDataInitialState,
      key,
      mode,
      tabStatus,
      ...visualBuilderData,
      ...values,
    };

    let gridView = initialPayload.gridView;
    let leftDrawer = initialPayload.leftDrawer;
    let leftDrawerAutoHide = initialPayload.leftDrawerAutoHide;

    // Handle tab status changes
    if (tabStatus === TAB_STATUSES.CUSTOMIZE && prevTabStatus === TAB_STATUSES.COLLECT && initialPayload.tabChanged) {
      const { visualBuilderData } = visualBuilderDataSelector({ ...params, tabStatus: TAB_STATUSES.COLLECT })(
        getState(),
      );
      gridView = visualBuilderData.gridView;
    }

    // Handle drawer state for shared views
    if (mode === VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION) {
      leftDrawer = false;
      leftDrawerAutoHide = true;
    }

    // Process and transform data
    const processedAndTransformedPayload = {
      ...initialPayload,
      gridView,
      leftDrawer,
      leftDrawerAutoHide,
      flags: flags.reduce((prev, val) => ({ ...prev, [val.name]: val.value }), {}),
      contentTypes,
      tags,
      mediaItems,
      assetCategories,
    };

    let shareDataUpdate: {
      scope?: string;
      currentMediaItemId?: number;
      primaryFilter?: EXPERIENCE_PRIMARY_FILTER;
      typeFilterId?: number;
      tagFilterId?: number;
      createdBy?: User;
      currentShareId?: number;
    } = {
      primaryFilter: processedAndTransformedPayload.primaryFilter,
    };

    // Handle public media share visualization
    if (
      (mode === VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION || mode === VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION) &&
      propertyId &&
      shareId
    ) {
      const { resp: share } = await PublicVisualizationService.getPublicMediaShare(propertyId, shareId);

      if (share) {
        shareDataUpdate = {
          ...shareDataUpdate,
          scope: share.options.scope,
          currentMediaItemId: share.options.currentMediaItemId,
          primaryFilter: share.options.primaryFilter,
          typeFilterId: share.options.typeFilterId,
          tagFilterId: share.options.tagFilterId,
          createdBy: share.createdBy,
          currentShareId: share.id,
        };
      }
    }

    const payloadWithShareData = { ...processedAndTransformedPayload, ...shareDataUpdate };

    let newTags = payloadWithShareData.tags;

    // Ensure otherTag is included
    if (
      payloadWithShareData.otherTag &&
      !payloadWithShareData.tags.find((tag: { id: number }) => tag.id === payloadWithShareData.otherTag?.id)
    ) {
      newTags = [...newTags, payloadWithShareData.otherTag];
    }

    let newGridView = payloadWithShareData.gridView;
    let newMediaItems = payloadWithShareData.mediaItems;

    // Handle single media item scope
    if (
      isPublicPage(payloadWithShareData.tabStatus) &&
      payloadWithShareData.scope &&
      payloadWithShareData.scope === SHARE_SCOPE.MEDIA_ITEM &&
      payloadWithShareData.mediaItems?.length
    ) {
      let initialMediaItem;

      if (payloadWithShareData.currentMediaItemId) {
        initialMediaItem = payloadWithShareData.mediaItems.find(
          (mediaItem: { id: number }) => mediaItem.id === payloadWithShareData.currentMediaItemId,
        );
      }

      if (!initialMediaItem) {
        initialMediaItem = payloadWithShareData.mediaItems[0];
      }

      newGridView = false;

      if (initialMediaItem) {
        newMediaItems = [initialMediaItem];
      }
    }

    const mediaItemScopePayload = {
      ...payloadWithShareData,
      gridView: newGridView,
      mediaItems: newMediaItems,
      tags: newTags,
    };

    // Transform data
    const transformedPayload = buildTransformedPayload(mediaItemScopePayload);
    const { assetCategoryTabData, contentTypeTabData } = transformedPayload;
    const transformedAcPayload = { ...transformedPayload, ...assetCategoryTabData };
    const transformedCtPayload = { ...transformedPayload, ...contentTypeTabData };

    // Sort data
    const sortedPayload = {
      ...buildSortedPayload(transformedPayload),
      propertyMenuItems,
    };
    const sortedAcPayload = {
      ...buildSortedPayload(transformedAcPayload),
    };
    const sortedCtPayload = {
      ...buildSortedPayload(transformedCtPayload),
    };

    // Apply filters
    const filteredPayload = buildFilteredPayload(sortedPayload);
    const filteredAcPayload = buildFilteredPayload(sortedAcPayload);
    const filteredCtPayload = buildFilteredPayload(sortedCtPayload);

    // Initialize if new
    let payloadWithId;

    if (filteredPayload?.id) {
      payloadWithId = {
        ...filteredPayload,
      };
    } else {
      payloadWithId = {
        ...filteredPayload,
        id: crypto.randomUUID(),
        initialized: true,
      };
      initial = true;
    }

    const finalPayload = {
      ...payloadWithId,
      assetCategoryTab: extractTabDependentPayload(filteredAcPayload),
      contentTypeTab: extractTabDependentPayload(filteredCtPayload),
      rightDrawer: false,
      rightDrawerAutoHide: true,
    };

    // Dispatch update action
    dispatch({
      type: GET_VISUAL_BUILDER_DATA,
      payload: finalPayload,
    });

    // Handle initial values based on mode
    if (initial) {
      if (mode === VB_MODE.PRESENTATION) {
        dispatch(addPresentationInitialValues(params));
      }
      if (mode === VB_MODE.VISUALIZATION) {
        dispatch(addVisualizationInitialValues(params));
      }
    }

    // Handle public visualization modes
    if (
      mode === VB_MODE.PUBLIC_VISUALIZATION ||
      mode === VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION ||
      mode === VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION
    ) {
      dispatch(addPublicVisualizationInitialValues(params));
    }
  };
};

/**
 * Action creator to refresh visual builder data
 * @param {Object} params - Configuration parameters
 * @returns {Function} Thunk action function
 */
function refreshVisualBuilderData(
  params: VisualBuilderDataSelectorParams,
): (dispatch: ThunkDispatch, getState: () => State) => Promise<void> {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { key, mode } = getVisualBuilderMode(params) as { key: string; mode: VB_MODE };
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    // Create payload with current state
    const payload = {
      key,
      mode,
      assetCategoryNames: {},
      experienceAssets: [],
      search: {},
      ...visualBuilderData,
    } as VisualBuilderDataState & {
      assetCategoryTab: VisualBuilderDataState;
      contentTypeTab: VisualBuilderDataState;
    };

    const { assetCategoryTab, contentTypeTab } = payload;
    const acPayload = { ...payload, ...assetCategoryTab };
    const ctPayload = { ...payload, ...contentTypeTab };

    // Re-apply sorting and filtering
    const sortedPayload = buildSortedPayload(payload);
    const sortedAcPayload = buildSortedPayload(acPayload);
    const sortedCtPayload = buildSortedPayload(ctPayload);
    const filteredPayload = buildFilteredPayload(sortedPayload);
    const filteredAcPayload = buildFilteredPayload(sortedAcPayload);
    const filteredCtPayload = buildFilteredPayload(sortedCtPayload);

    const finalPayload = {
      ...filteredPayload,
      assetCategoryTab: extractTabDependentPayload(filteredAcPayload),
      contentTypeTab: extractTabDependentPayload(filteredCtPayload),
      key,
    };

    // Update state
    dispatch({
      type: UPDATE_VISUAL_BUILDER_DATA,
      payload: finalPayload,
    });

    // Handle public visualization modes
    if (
      mode === VB_MODE.PUBLIC_VISUALIZATION ||
      mode === VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION ||
      mode === VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION
    ) {
      dispatch(addPublicVisualizationInitialValues(params));
    }
  };
}

/**
 * Action creator to update visual builder data
 * @param {Object} params - Configuration parameters
 * @param {Object} values - Values to update
 * @param {Object} options - Additional options
 * @param {boolean} refresh - Whether to refresh data after update
 * @returns {Function} Thunk action function
 */
export const updateVisualBuilderData = (
  params: VisualBuilderDataSelectorParams,
  values: Record<string, unknown>,
  options: Record<string, unknown> = {},
  refresh: boolean = false,
): ((dispatch: ThunkDispatch, getState: () => State) => Promise<void>) => {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { key, mode } = getVisualBuilderMode(params) as { key: string; mode: VB_MODE };

    // Update visual builder state
    dispatch({
      type: UPDATE_VISUAL_BUILDER_DATA,
      payload: { key, ...values, ...options },
    });

    // Update experience if in visualization mode
    if (params.propertyId && params.experienceId && mode === VB_MODE.VISUALIZATION && Object.keys(values).length) {
      await ExperienceService.updateExperience(params.propertyId, params.experienceId, values);
    }

    // Handle voice memo playback state
    const voiceMemoPlayback = getState().simpleValues?.["voice-memo-playback"];

    if (
      values.currentMediaItemId &&
      voiceMemoPlayback?.element?.positioning === ELEMENT_POSITIONING.CONTENT &&
      voiceMemoPlayback?.element?.mediaItemId !== values.currentMediaItemId &&
      voiceMemoPlayback?.open
    ) {
      // Close voice memo playback when switching content
      dispatch(setSimpleValue("voice-memo-playback", { open: false }));
    }

    // Refresh data if requested
    if (refresh) {
      dispatch(refreshVisualBuilderData(params));
    }
  };
};

/**
 * Action creator to reset visual builder data
 * @param {Object} params - Configuration parameters
 * @returns {Function} Thunk action function
 */
export const resetVisualBuilderData = (
  params: VisualBuilderDataSelectorParams,
): ((dispatch: ThunkDispatch, getState: () => State) => Promise<void>) => {
  return async (dispatch: ThunkDispatch) => {
    const { key } = getVisualBuilderMode(params) as { key: string; mode: VB_MODE };

    // Reset visual builder state
    dispatch({
      type: RESET_VISUAL_BUILDER_DATA,
      payload: { key },
    });
  };
};
